import * as React from 'react';
import { observer } from 'mobx-react';

import ContentModal from 'shared/newComponents/modals/ContentModal';
import { BaseModalProps } from 'shared/newEntities/components/modals';
import { useLocalStore } from 'shared/entities/common/hooks';
import {
  ChannelsModalStore,
  ChannelsModalStoreProvider
} from 'stores/ChannelsModalStore';
import { useChannelsStore, useRootStore } from 'stores';
import { ScenarioBaseModel } from 'shared/models/scenario';
import ChannelsContent from 'shared/newContainers/ChannelsContent';
import ChannelAccounts from 'shared/newComponents/ChannelAccounts';
import { ChannelSelectProps } from 'shared/entities/channelsModal';
import { useTypedTranslation } from 'shared/entities/localization';
import ChannelScenariosNavigationConfirmModal from 'shared/newContainers/ChannelsContent/ChannelScenariosNavigationConfirmModal';

import ConnectChannelsFooter from './ConnectChannelsFooter';

import './ChannelsModal.modules.scss';

type Props = BaseModalProps & {
  scenario?: ScenarioBaseModel;
  withAccounts?: boolean;
  footer?: React.ReactNode;
  channelSelectProps?: ChannelSelectProps | null;
};
const ChannelsModal: React.FC<Props> = ({
  scenario,
  withAccounts = true,
  onClose,
  opened,
  footer,
  channelSelectProps
}) => {
  const rootStore = useRootStore();
  const channelsStore = useChannelsStore();
  const { t } = useTypedTranslation('newContainers');
  const storeCtx = useLocalStore(
    () =>
      new ChannelsModalStore({
        rootStore,
        scenario: scenario || null,
        withAccounts,
        isModal: true,
        channelSelectProps
      })
  );
  const channelScenariosStore = storeCtx.store.channelScenariosStore;

  const getChannelChangeModalDescription = ({
    editedChannelName,
    pendingChannelChangeIsSelection
  }: {
    editedChannelName: string | null;
    pendingChannelChangeIsSelection: boolean;
  }) => {
    if (pendingChannelChangeIsSelection) {
      return t(
        editedChannelName
          ? 'ChannelsModal.Content.scenarioActions.saveChanges.beforeChannelChangeWithChannelName'
          : 'ChannelsModal.Content.scenarioActions.saveChanges.beforeChannelChange',
        { channelName: editedChannelName }
      );
    }

    return t(
      editedChannelName
        ? 'ChannelsModal.Content.scenarioActions.saveChanges.beforeLeaveWithChannelName'
        : 'ChannelsModal.Content.scenarioActions.saveChanges.beforeLeave',
      { channelName: editedChannelName }
    );
  };

  React.useEffect(() => {
    if (opened) {
      channelsStore.load();
    }
  }, [opened]);

  React.useEffect(() => {
    storeCtx.store.setChannelSelectionProps(channelSelectProps);
  }, [channelSelectProps, storeCtx.store]);

  React.useEffect(() => {
    return () => {
      storeCtx.store.disposeAll();
    };
  }, []);

  const handleClose = React.useCallback(() => {
    if (!storeCtx.store.requestClose()) {
      return;
    }

    storeCtx.store.unselect();
    onClose();
  }, [onClose, storeCtx.store]);

  const getContentFooter = (): React.ReactNode => {
    if (footer !== null && footer !== undefined) {
      return footer;
    }

    if (storeCtx.store.showSelectedChannelScenarios) {
      return <ConnectChannelsFooter />;
    }

    return undefined;
  };

  return (
    <ChannelsModalStoreProvider
      value={{ store: storeCtx.store, close: handleClose }}
    >
      <ContentModal
        onClose={handleClose}
        opened={opened}
        title={(t) =>
          t('ChannelsModal.title', {
            ns: 'newContainers'
          })
        }
        headerExtraComponent={
          !storeCtx.store.scenario && storeCtx.store.withAccounts ? (
            <ChannelAccounts
              styleName="channels-modal__accounts"
              onClick={handleClose}
            />
          ) : undefined
        }
      >
        <ChannelsContent footer={getContentFooter()} />
      </ContentModal>
      {channelScenariosStore && (
        <ChannelScenariosNavigationConfirmModal
          channelsModalStore={storeCtx.store}
          channelScenariosStore={channelScenariosStore}
          title={t('ChannelsModal.Content.scenarioActions.saveChanges.title')}
          discardText={t(
            'ChannelsModal.Content.scenarioActions.saveChanges.discard'
          )}
          saveText={t('ChannelsModal.Content.scenarioActions.saveChanges.save')}
          getDescription={getChannelChangeModalDescription}
          onPendingCloseApplied={onClose}
        />
      )}
    </ChannelsModalStoreProvider>
  );
};

export default observer(ChannelsModal);
